import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaBed, FaPlane, FaExternalLinkAlt, FaTicketAlt, FaTaxi, FaCar } from 'react-icons/fa';
import {
  getHotelBookingLinksPublic,
  getFlightBookingLinksPublic,
  getAttractionBookingLinksPublic,
  getTransferBookingLinksPublic,
  getCarBookingLinksPublic,
  trackAffiliateClickPublic,
  trackEvent,
} from '../api/client';
import { trackBookingClick } from './RetargetingPixels';
import {
  defaultStayWindow,
  getAnonSessionId,
  getPendingTripContext,
  setPendingTripContext,
  toISODate,
} from '../utils/tripContext';

/** The non-bed, non-seat products, all monetised by the same CJ link. */
const EXTRA_ROWS = [
  { key: 'attraction', linkType: 'attraction', label: 'Tickets & attractions', Icon: FaTicketAlt },
  { key: 'transfer', linkType: 'transfer', label: 'Airport transfer', Icon: FaTaxi },
  { key: 'car', linkType: 'car', label: 'Car hire', Icon: FaCar },
];

/**
 * BookingBlock — the booking surface for anonymous pages (blog articles, the
 * free preview, the public samples).
 *
 * It is deliberately not the same component as BookingOptions, which is built
 * for a logged-in itinerary: this one renders real anchors (so the first click
 * reaches Booking.com), marks them rel="sponsored nofollow" so 124 indexed
 * article URLs don't put their own rankings at risk, carries the affiliate
 * disclosure, and calls the public affiliate endpoints with plain axios — never
 * apiClient, whose 401 handler would navigate a reader away mid-article.
 *
 * The date pill does double duty: it is a Booking.com parameter and it is the
 * least intrusive trip-intent form that can be put on a content page. Whatever
 * the reader picks is stashed as pending trip context and carried into the
 * planner by the "Plan this trip" CTA.
 *
 * @param {string} destination     City/region to search — e.g. BlogArticle.destination
 * @param {string} surface         Per-surface CJ sub-ID, e.g. `blog_${slug}`
 * @param {string} heading         Optional heading override
 * @param {boolean} showFlights    Render the "Getting there" flight row
 * @param {string} initialCheckIn  Dates the surface already knows (a sample's
 * @param {string} initialCheckOut trip window); otherwise a rolling default
 * @param {string} initialOrigin   Origin the surface already knows, so the
 *                                 flight row can render without being asked
 * @param {boolean} showExtras     Render the tickets / transfer / car hire row
 */
const BookingBlock = ({
  destination,
  surface,
  heading,
  showFlights = true,
  initialCheckIn,
  initialCheckOut,
  initialOrigin = '',
  showExtras = true,
  className = '',
}) => {
  // A surface that already knows the trip window uses it; the rest fall back to
  // a rolling near-future one, because an undated Booking.com search returns an
  // empty page with no prices.
  const initialWindow = useRef(
    initialCheckIn && initialCheckOut
      ? { checkIn: initialCheckIn, checkOut: initialCheckOut }
      : defaultStayWindow()
  ).current;
  // Booking.com returns nothing for a stay in the past.
  const today = useRef(toISODate(new Date())).current;
  const [checkIn, setCheckIn] = useState(initialWindow.checkIn);
  const [checkOut, setCheckOut] = useState(initialWindow.checkOut);
  const [datesTouched, setDatesTouched] = useState(false);
  const [hotelLinks, setHotelLinks] = useState(null);
  const [hotelError, setHotelError] = useState(false);
  // A visitor who has already said where they're flying from on another surface
  // should not be asked twice.
  const resolvedOrigin = useRef(
    initialOrigin || getPendingTripContext()?.origin || ''
  ).current;
  const [origin, setOrigin] = useState(resolvedOrigin);
  const [flightLinks, setFlightLinks] = useState(null);
  const [flightLoading, setFlightLoading] = useState(false);
  const [flightError, setFlightError] = useState('');
  const [extras, setExtras] = useState(null);
  const [extrasLoading, setExtrasLoading] = useState(false);
  const containerRef = useRef(null);
  const viewTracked = useRef(false);

  // Fetch on mount and whenever the dates change, so the primary button is a
  // live href by the time it is clicked — one click, not two.
  useEffect(() => {
    if (!destination) return;
    let cancelled = false;
    setHotelError(false);
    // No named property on a content page — search the destination itself.
    getHotelBookingLinksPublic('', destination, checkIn, checkOut, surface)
      .then((data) => {
        if (!cancelled) setHotelLinks(data);
      })
      .catch(() => {
        if (!cancelled) setHotelError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [destination, checkIn, checkOut, surface]);

  // Refresh flight links when dates change, but only once the reader has asked
  // for them by supplying an origin.
  useEffect(() => {
    if (!flightLinks || !origin) return;
    let cancelled = false;
    getFlightBookingLinksPublic(origin, destination, checkIn, checkOut, surface)
      .then((data) => {
        if (!cancelled) setFlightLinks(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // flightLinks is intentionally omitted — this refreshes existing results only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIn, checkOut]);

  // A surface that already knows where the trip starts shouldn't make the
  // visitor type it again — load the flight row straight away.
  useEffect(() => {
    if (!showFlights || !destination || !resolvedOrigin) return;
    let cancelled = false;
    getFlightBookingLinksPublic(resolvedOrigin, destination, checkIn, checkOut, surface)
      .then((data) => {
        if (!cancelled) setFlightLinks(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // Dates are handled by the refresh effect above; this runs for the origin only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination, resolvedOrigin, showFlights, surface]);

  // "Viewed" means actually scrolled into view, not merely mounted.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !viewTracked.current) {
          viewTracked.current = true;
          trackEvent('booking_block_viewed', { surface, destination });
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [surface, destination]);

  const handleDateChange = useCallback(
    (which, value) => {
      if (!value) return;
      setDatesTouched(true);
      if (which === 'in') {
        setCheckIn(value);
        // Keep the window valid: push checkout to the day after check-in.
        if (value >= checkOut) {
          const next = new Date(`${value}T00:00:00`);
          next.setDate(next.getDate() + 1);
          // toISODate, not toISOString — the latter converts to UTC and would
          // hand back the *previous* day for any reader east of Greenwich.
          const nextStr = toISODate(next);
          setCheckOut(nextStr);
          setPendingTripContext({ destination, startDate: value, endDate: nextStr, source: surface });
          return;
        }
        setPendingTripContext({ destination, startDate: value, endDate: checkOut, source: surface });
      } else {
        setCheckOut(value);
        setPendingTripContext({ destination, startDate: checkIn, endDate: value, source: surface });
      }
      trackEvent('booking_block_dates_set', { surface, destination });
    },
    [checkIn, checkOut, destination, surface]
  );

  const handleAffiliateClick = (partnerKey, url, linkType) => {
    // A visitor who clicks through to a partner but never registers is the
    // warmest anonymous lead there is, so their trip is recorded here as well
    // as on the date pill — it is replayed into a real itinerary if they come
    // back and sign up.
    setPendingTripContext({
      destination,
      startDate: checkIn,
      endDate: checkOut,
      origin: linkType === 'flight' ? origin : undefined,
      source: surface,
      clickedPartner: partnerKey,
    });
    // Fire-and-forget; the anchor's own navigation is never blocked.
    trackAffiliateClickPublic(partnerKey, linkType, url, {
      city: destination,
      destination,
      origin: linkType === 'flight' ? origin : null,
      surface,
      subId: surface,
      sessionId: getAnonSessionId(),
      checkIn,
      checkOut,
    }).catch(() => {});
    trackEvent(linkType === 'flight' ? 'booking_click_flight' : 'booking_click_hotel', {
      surface,
      destination,
      partner: partnerKey,
    });
    // A visitor who clicks through but never registers is the warmest anonymous
    // lead there is — and the only way to reach them again is the ad platforms.
    trackBookingClick(destination, linkType, partnerKey);
  };

  const loadFlights = async (e) => {
    e.preventDefault();
    const from = origin.trim();
    if (from.length < 2) {
      setFlightError('Enter the city you are flying from');
      return;
    }
    setFlightError('');
    setFlightLoading(true);
    setPendingTripContext({ destination, origin: from, source: surface });
    trackEvent('booking_block_flights_requested', { surface, destination });
    try {
      const data = await getFlightBookingLinksPublic(from, destination, checkIn, checkOut, surface);
      setFlightLinks(data);
    } catch {
      setFlightError('Could not load flight options right now.');
    } finally {
      setFlightLoading(false);
    }
  };

  const loadExtras = async () => {
    setExtrasLoading(true);
    trackEvent('booking_block_extras_requested', { surface, destination });
    try {
      // One round trip each, fired together — a partial result is still useful,
      // so a failure in one product doesn't hide the other two.
      const [attraction, transfer, car] = await Promise.allSettled([
        getAttractionBookingLinksPublic('', destination, checkIn, surface),
        getTransferBookingLinksPublic(destination, checkIn, surface),
        getCarBookingLinksPublic(destination, checkIn, checkOut, surface),
      ]);
      const value = (r) => (r.status === 'fulfilled' ? r.value : null);
      setExtras({
        attraction: value(attraction),
        transfer: value(transfer),
        car: value(car),
      });
    } finally {
      setExtrasLoading(false);
    }
  };

  if (!destination) return null;

  // If the API is unreachable the block still has to be useful, so fall back to
  // a plain (untracked) dated search rather than rendering a dead card.
  const fallbackHotelUrl =
    `https://www.booking.com/searchresults.html?ss=${encodeURIComponent(destination)}` +
    `&checkin=${checkIn}&checkout=${checkOut}`;
  const primaryHotel = hotelLinks?.booking;
  const primaryHotelUrl = primaryHotel?.url || (hotelError ? fallbackHotelUrl : null);
  const otherHotels = hotelLinks
    ? Object.entries(hotelLinks).filter(([key]) => key !== 'booking')
    : [];
  const primaryFlight = flightLinks?.booking || flightLinks?.google_flights;
  const primaryFlightKey = flightLinks?.booking ? 'booking' : 'google_flights';
  const otherFlights = flightLinks
    ? Object.entries(flightLinks).filter(([key]) => key !== primaryFlightKey)
    : [];

  const nights = Math.max(
    1,
    Math.round((new Date(checkOut) - new Date(checkIn)) / (24 * 60 * 60 * 1000))
  );

  return (
    <aside
      ref={containerRef}
      className={`my-10 rounded-xl border border-neutral-200 bg-white shadow-sm overflow-hidden ${className}`}
    >
      <div className="px-5 py-4 sm:px-6 sm:py-5">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-neutral-400 mb-2">
          <FaBed className="text-neutral-400" /> Where to stay
        </div>
        <h3 className="font-serif text-xl font-bold text-neutral-900 mb-1 leading-snug">
          {heading || `Where to stay in ${destination}`}
        </h3>
        <p className="text-[0.95rem] text-neutral-600 leading-relaxed mb-4">
          We checked live availability for {destination}
          {datesTouched ? '' : ' over a sample near-future window'} — {nights} night
          {nights === 1 ? '' : 's'} from {checkIn}. Change the dates to match your trip.
        </p>

        {/* Date pill — also the least intrusive trip-intent form on the page */}
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <label className="text-sm">
            <span className="block text-xs font-medium text-neutral-500 mb-1">Check in</span>
            <input
              type="date"
              value={checkIn}
              min={today}
              onChange={(e) => handleDateChange('in', e.target.value)}
              className="px-3 py-2 border border-neutral-300 rounded-lg text-sm text-neutral-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </label>
          <label className="text-sm">
            <span className="block text-xs font-medium text-neutral-500 mb-1">Check out</span>
            <input
              type="date"
              value={checkOut}
              min={checkIn}
              onChange={(e) => handleDateChange('out', e.target.value)}
              className="px-3 py-2 border border-neutral-300 rounded-lg text-sm text-neutral-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </label>
        </div>

        {primaryHotelUrl ? (
          <a
            href={primaryHotelUrl}
            target="_blank"
            rel="sponsored nofollow noopener noreferrer"
            onClick={() => handleAffiliateClick('booking', primaryHotelUrl, 'hotel')}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm no-underline"
          >
            <FaBed size={14} />
            See places to stay in {destination}
            <FaExternalLinkAlt size={10} className="opacity-80" />
          </a>
        ) : (
          <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-neutral-200 text-neutral-500 text-sm font-semibold rounded-lg">
            Loading stays…
          </span>
        )}

        {otherHotels.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="text-xs text-neutral-400 uppercase tracking-wide">Also on</span>
            {otherHotels.map(([key, partner]) => (
              <a
                key={key}
                href={partner.url}
                target="_blank"
                rel="sponsored nofollow noopener noreferrer"
                onClick={() => handleAffiliateClick(key, partner.url, 'hotel')}
                className="text-neutral-600 hover:text-primary-600 underline decoration-neutral-300 underline-offset-2"
              >
                {partner.name}
              </a>
            ))}
          </div>
        )}

        {/* Getting there — a flight search needs an origin, so ask for one */}
        {showFlights && (
          <div className="mt-6 pt-5 border-t border-neutral-100">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-neutral-400 mb-2">
              <FaPlane className="text-neutral-400" /> Getting there
            </div>
            {!flightLinks ? (
              <form onSubmit={loadFlights} className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                  placeholder="Flying from…"
                  aria-label={`City you are flying from to ${destination}`}
                  className="px-3 py-2 border border-neutral-300 rounded-lg text-sm text-neutral-800 focus:outline-none focus:ring-2 focus:ring-primary-400 flex-1 min-w-[10rem]"
                />
                <button
                  type="submit"
                  disabled={flightLoading}
                  className="px-4 py-2 text-sm font-semibold text-neutral-700 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors disabled:opacity-60"
                >
                  {flightLoading ? 'Checking…' : 'Find flights'}
                </button>
              </form>
            ) : (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                {primaryFlight && (
                  <a
                    href={primaryFlight.url}
                    target="_blank"
                    rel="sponsored nofollow noopener noreferrer"
                    onClick={() => handleAffiliateClick(primaryFlightKey, primaryFlight.url, 'flight')}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-800 hover:bg-neutral-900 text-white text-sm font-semibold rounded-lg transition-colors no-underline"
                  >
                    <FaPlane size={12} />
                    {origin} → {destination} on {primaryFlight.name}
                  </a>
                )}
                {otherFlights.map(([key, partner]) => (
                  <a
                    key={key}
                    href={partner.url}
                    target="_blank"
                    rel="sponsored nofollow noopener noreferrer"
                    onClick={() => handleAffiliateClick(key, partner.url, 'flight')}
                    className="text-sm text-neutral-600 hover:text-primary-600 underline decoration-neutral-300 underline-offset-2"
                  >
                    {partner.name}
                  </a>
                ))}
              </div>
            )}
            {flightError && <p className="mt-2 text-xs text-red-600">{flightError}</p>}
          </div>
        )}

        {/* On the ground — tickets, the ride in from the airport, and a car if
            the trip needs one. Loaded lazily: these answer questions a visitor
            only has once they've decided they're going, so they shouldn't cost
            three requests on every article view. */}
        {showExtras && (
          <div className="mt-6 pt-5 border-t border-neutral-100">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-neutral-400 mb-2">
              <FaTicketAlt className="text-neutral-400" /> On the ground
            </div>
            {!extras ? (
              <button
                type="button"
                onClick={loadExtras}
                disabled={extrasLoading}
                className="text-sm font-medium text-neutral-600 hover:text-primary-600 underline decoration-neutral-300 underline-offset-2 disabled:opacity-60"
              >
                {extrasLoading
                  ? 'Loading…'
                  : `Tickets, airport transfers and car hire in ${destination}`}
              </button>
            ) : (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                {EXTRA_ROWS.map(({ key, linkType, label, Icon }) => {
                  const partner = extras[key]?.booking;
                  if (!partner) return null;
                  return (
                    <a
                      key={key}
                      href={partner.url}
                      target="_blank"
                      rel="sponsored nofollow noopener noreferrer"
                      onClick={() => handleAffiliateClick('booking', partner.url, linkType)}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors no-underline"
                    >
                      <Icon size={12} className="text-neutral-500" />
                      {label}
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <p className="px-5 sm:px-6 py-3 bg-neutral-50 border-t border-neutral-100 text-xs text-neutral-500">
        We may earn a commission from bookings made through these links, at no extra cost to you.
      </p>
    </aside>
  );
};

export default BookingBlock;
